import {api} from "@/shared/api";

import {DialogMode} from "@/shared/recording/recordDialog";
import {DescriptionTypeahead} from "@/shared/ui/descriptionTypeahead";
import {Combobox} from "@/shared/ui/combobox";
import {SubstituteSelection} from "@/shared/workingtime/substituteSelection";
import {isWorkingTimeTypeChangeAllowed} from "@/shared/securityHelper";
import {SubstituteValidation} from "@/shared/workingtime/substituteValidation";
import {WorkingTimeDateSpanRevisionsTable} from "./workingTimeDateSpanRevisionsTable";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {SecurityStatus} from "@/shared/securityStatus";
import {daysBetween, formatDate, getISODate, isSameDay, parseDateInput, parseISODate} from "@/shared/utils/datefunctions";
import {User, WorkingTimeDateSpan, WorkingTimeType} from "@/shared/timr";
import {DurationUnit, WorkingTimeTypeCategory} from "@/shared/timrTypes";
import {ValidationsListDialogComponent, ValidationsListItem} from "@/shared/validations/validationsListComponent";

enum WorkingTimeDateSpanDialogMode { Edit = "Edit", Show = "Show", Duplicate = "Duplicate" }

export interface WorkingTimeDateSpanDialogConfig {
	workingTimeTypes: WorkingTimeType[];
	dialogId: string;
	userSelectionEnabled?: boolean;
	title?: {
		[key in keyof typeof DialogMode]: string
	};
	size?: BootstrapModal.ModalSize;
}

export class WorkingTimeDateSpanDialog {

	public static readonly EVENT_DATE_SPAN_EDITED = "workingTimeDateSpanEdited";
	public static readonly EVENT_DATE_SPAN_DELETED = "workingTimeDateSpanDeleted";

	protected dialog: BootstrapModal.Modal;
	protected dialogMode: WorkingTimeDateSpanDialogMode;
	protected dom: JQuery;
	private editUnit: DurationUnit;
	private workingTimeDateSpan: WorkingTimeDateSpan;
	private workingTimeDateSpanRevisionsTable: WorkingTimeDateSpanRevisionsTable;
	private descriptionTypeahead: DescriptionTypeahead;
	private daysDateDifference: number;
	private userCombobox: Combobox<User>;
	private substituteSelection?: SubstituteSelection;
	private substituteValidation?: SubstituteValidation;
	private validationsList?: ValidationsListDialogComponent;

	private currentRequestCount = 0;

	constructor(public config: WorkingTimeDateSpanDialogConfig) {
		this.config = config;
		this.config.size = this.config.size || "modal-xl";

		this.dom = $(this.config.dialogId);
		this.init();

		this.initChangeListeners();
		this.initWorkingTimeDateSpanRevisionsTable();
		this.initWorkingTimeTypeSelect();
		this.initUserCombobox();
		this.initSubstitutes();
	}

	private init() {
		this.dialog = new BootstrapModal.RecordModal({
			size: this.config.size,
			title: this.config.title ? this.config.title[this.dialogMode] : undefined,
			message: this.dom,
			css: {
				content: "border-NOT_INITIALIZED"
			},
			buttons: [{
				text: "dialog.delete",
				action: () => {
					this._delete();
				},
				css: "deleteButton btn-outline-danger",
				left: true
			}, {
				text: "dialog.save",
				action: () => {
					this.checkConfigurationAndSave(this.dialog);
				},
				css: "saveButton btn-primary"
			}, {
				text: "dialog.cancel",
				action: () => {
					this.closeDialog();
				},
				css: "cancelButton btn-default"
			}],
			checkbox: false,
			onOpen: () => {
				this.updateBorder();
				this.initButtonLoadingListeners();
			},
			onClose: () => {
				this.closeDialog();
			}
		});

		this.descriptionTypeahead = new DescriptionTypeahead({
			element: this.dom.find(".descriptionTypeahead"),
			url: "filterWorkingTimeDescriptions.json"
		});

		if (globals.savedValidationsEnabled) {
			this.validationsList = document.createElement("validations-list-dialog") as ValidationsListDialogComponent;
			this.dom.find("#workingTimeDateSpanDurationContainer").append(this.validationsList);
		}
	}

	private initWorkingTimeDateSpanRevisionsTable() {
		this.workingTimeDateSpanRevisionsTable = new WorkingTimeDateSpanRevisionsTable({
			element: $("#workingTimeDateSpanRevisionsTable"),
			orderable: false,
			clampNote: true,
			allowExpand: true
		});
		this.dom.find("#workingTimeDateSpanRevisionTableCollapseToggle").click(() => {
			this.dom.find("#workingTimeDateSpanRevisionTableCollapse").collapse("toggle");
		});
		this.dom.find("#workingTimeDateSpanRevisionTableCollapse")
			.on("show.bs.collapse", () => {
				this.dom.find("#workingTimeDateSpanRevisionTableCollapseTextShow").hide();
				this.dom.find("#workingTimeDateSpanRevisionTableCollapseTextHide").show();
			})
			.on("hide.bs.collapse", () => {
				this.dom.find("#workingTimeDateSpanRevisionTableCollapseTextShow").show();
				this.dom.find("#workingTimeDateSpanRevisionTableCollapseTextHide").hide();
			});
	}

	private initChangeListeners() {
		this.dom.find(".endDate, .startHalfDay, .endHalfDay").change(() => {
			this.updateDuration();
		});

		this.dom.find("#workingTimeDateSpanDialogWorkingTimeType").change(() => {
			this.onSelectedWorkingTimeTypeChanged();
		});

		this.dom.find(".endDate").on("focusout", () => {
			const startDate = parseDateInput(this.dom.find(".startDate").val() as string);
			const endDate = parseDateInput(this.dom.find(".endDate").val() as string);
			this.daysDateDifference = daysBetween(startDate, endDate);
		});

		this.dom.find(".startDate").on("apply.daterangepicker focusout", () => {
			this.updateEndDateTime();
			this.updateDuration();
		});

		this.dom.find(".endDate").on("apply.daterangepicker focusout", () => {
			this.updateDuration();
		});

		this.dom.find(".endDate, .startDate").change(() => {
			this.substituteValidation?.setStartEndDate(
				parseDateInput(this.dom.find(".startDate").val() as string),
				parseDateInput(this.dom.find(".endDate").val() as string)
			);
		});

	}

	private onWorkingTimeDateSpanEdited() {
		$(this).triggerHandler(WorkingTimeDateSpanDialog.EVENT_DATE_SPAN_EDITED);
	}

	private onWorkingTimeDateSpanDeleted() {
		$(this).triggerHandler(WorkingTimeDateSpanDialog.EVENT_DATE_SPAN_DELETED);
	}

	public show(workingTimeDateSpan: WorkingTimeDateSpan, validationsListItem?: ValidationsListItem) {
		this.dialogMode = WorkingTimeDateSpanDialogMode.Show;
		this.showDateSpan(workingTimeDateSpan, false, validationsListItem);
		this.updateActionButtonVisibility();
		this.disableFields(true);
	}

	public edit(workingTimeDateSpan: WorkingTimeDateSpan, validationsListItem?: ValidationsListItem) {
		this.dialogMode = WorkingTimeDateSpanDialogMode.Edit;
		this.showDateSpan(workingTimeDateSpan, false, validationsListItem);
		this.updateActionButtonVisibility();
		this.disableFields(false);
		this.userCombobox.setDisabled(true);
	}

	public duplicate(workingTimeDateSpan: WorkingTimeDateSpan, showUserSelection: boolean = false) {
		this.dialogMode = WorkingTimeDateSpanDialogMode.Duplicate;
		api("reports/filterUsers.json")
			.get({
				showAllUsers: true,
				activeUsersOnly: true,
				hideCurrentUserIfGroupLeader: true
			})
			.done(data => {
				if (data?.users) {
					this.userCombobox.updateItems(data.users, workingTimeDateSpan.user);
					this.showDateSpan(workingTimeDateSpan, showUserSelection);
					this.updateActionButtonVisibility();
					this.disableFields(false);
					this.userCombobox.setDisabled(false);
				}
			});
	}

	private closeDialog() {
		this.dialog.close();
	}

	private showDateSpan(workingTimeDateSpan: WorkingTimeDateSpan, showUserSelection = false, validationsListItem?: ValidationsListItem) {
		this.workingTimeDateSpan = workingTimeDateSpan;
		// A duplicate is a new date span → compute the requirement from the current config (null).
		// Editing/showing an existing one → use its stored requirement
		this.substituteSelection?.setSubstitute(
			workingTimeDateSpan.substitute,
			this.dialogMode === WorkingTimeDateSpanDialogMode.Duplicate ? null : workingTimeDateSpan.requiresSubstitute
		);
		if (this.validationsList) {
			this.validationsList.validationsListItem = validationsListItem;
		}

		this.dom.find("#workingTimeDateSpanDialogUserRow").toggle(
			this.dialogMode === WorkingTimeDateSpanDialogMode.Duplicate
				? showUserSelection
				: !!this.config.userSelectionEnabled
		);

		if (this.dialogMode !== WorkingTimeDateSpanDialogMode.Duplicate) {
			this.userCombobox.updateItems([workingTimeDateSpan.user], workingTimeDateSpan.user);
		}
		this.workingTimeDateSpanRevisionsTable.filterByWorkingTimeDateSpanId(workingTimeDateSpan.id);

		this.toggleWorkingTimeCollapse();

		if (this.dialogMode !== WorkingTimeDateSpanDialogMode.Duplicate) {
			this.dom.find(".id").val(workingTimeDateSpan.id);
			this.dom.find(".version").val(workingTimeDateSpan.version);
		} else {
			this.dom.find(".id").val("");
			this.dom.find(".version").val(0);
		}
		this.dom.find(".userId").val(workingTimeDateSpan.user.id);
		this.dom.find(".startHalfDay").prop("checked", workingTimeDateSpan.startHalfDay);
		this.dom.find(".endHalfDay").prop("checked", workingTimeDateSpan.endHalfDay);
		const startDate = parseISODate(workingTimeDateSpan.start);
		this.dom.find(".startDate").val(formatDate(startDate));
		const endDate = parseISODate(workingTimeDateSpan.end);
		this.dom.find(".endDate").val(formatDate(endDate));
		this.dom.find(".description").val(workingTimeDateSpan.description);
		this.initWorkingTimeTypeSelect(workingTimeDateSpan.workingTimeType.id);
		this.dom.find("#workingTimeDateSpanDialogWorkingTimeType").val(workingTimeDateSpan.workingTimeType.id.toString());

		if (this.dialogMode !== WorkingTimeDateSpanDialogMode.Show) {
			this.daysDateDifference = daysBetween(startDate, endDate);
		}

		this.onSelectedWorkingTimeTypeChanged();
		this.substituteValidation?.setStartEndDate(startDate, endDate);

		this.dialog.show();
	}

	private toggleWorkingTimeCollapse() {
		const showWorkingTimeCollapse = this.dialogMode !== WorkingTimeDateSpanDialogMode.Duplicate;

		const showStatusDescriptionRow = this.dialogMode === WorkingTimeDateSpanDialogMode.Edit ||
			this.dialogMode === WorkingTimeDateSpanDialogMode.Show;

		this.dom.find("#workingTimeDateSpanRevisionTableCollapse").collapse(showWorkingTimeCollapse ? "hide" : "show");
		this.dom.find("#workingTimeDateSpanDialogChangeDescriptionRow").toggle(showStatusDescriptionRow);
		this.dom.find("#workingTimeDateSpanHistoryRow").toggle(this.dialogMode !== WorkingTimeDateSpanDialogMode.Duplicate);
	}

	private disableFields(disabled: boolean, overrideSelectorsDisabled?: string[]) {
		const inputFields = this.dom.find("input, textarea, select, button");
		inputFields.prop("disabled", disabled);
		if (overrideSelectorsDisabled) {
			this.dom.find(overrideSelectorsDisabled.join()).prop("disabled", true);
		}
	}

	private initWorkingTimeTypeSelect(workingTimeTypeId?: number) {
		const workingTimeTypeSelect = $("#workingTimeDateSpanDialogWorkingTimeType");
		const old: number = workingTimeTypeSelect.val() as number;
		workingTimeTypeSelect.empty();

		const currentWorkingTimeType = workingTimeTypeId ? this.getWorkingTimeTypeById(workingTimeTypeId) : null;
		this.config.workingTimeTypes.filter(wtt => {
			return !currentWorkingTimeType && !wtt.archived ||
				currentWorkingTimeType && isWorkingTimeTypeChangeAllowed(currentWorkingTimeType, wtt, true) &&
				wtt.category === currentWorkingTimeType.category;
		}).forEach(wtt => {
			workingTimeTypeSelect.append($("<option></option>", {value: wtt.id}).text(wtt.name));
		});

		if (old != null && !workingTimeTypeId) {
			workingTimeTypeSelect.val(old);
			this.onSelectedWorkingTimeTypeChanged();
		}
	}

	protected updateBorder() {
		const modalContent = this.dom.parents("div.modal-content");

		modalContent.removeClass("border-NOT_INITIALIZED");

		Object.keys(WorkingTimeTypeCategory)
			.map(k => WorkingTimeTypeCategory[k as keyof typeof WorkingTimeTypeCategory])
			.forEach(c => {
				modalContent.removeClass("border-" + c);

			});

		if (this.getSelectedWorkingTimeType() !== null) {
			modalContent.addClass("border-" + this.getSelectedWorkingTimeType().category);
		}
	}

	private updateActionButtonVisibility() {
		this.toggleEachStatusButtonVisibility(
			this.dialogMode !== WorkingTimeDateSpanDialogMode.Show,
			this.dialogMode === WorkingTimeDateSpanDialogMode.Edit
		);
	}

	private toggleEachStatusButtonVisibility(saveButton: boolean, deleteButton: boolean) {
		this.dialog.toggleButton("saveButton", saveButton);
		this.dialog.toggleButton("deleteButton", deleteButton);
	}

	private getSelectedWorkingTimeType(): WorkingTimeType {
		return this.getWorkingTimeTypeById(parseInt($("#workingTimeDateSpanDialogWorkingTimeType").val() as string, 10));
	}

	private getWorkingTimeTypeById(id: number): WorkingTimeType {
		const foundWorkingTimeTypes = $.grep(this.config.workingTimeTypes, (workingTimeType: WorkingTimeType, index: number) => {
			return workingTimeType.id === id;
		});
		return foundWorkingTimeTypes.length > 0 ? foundWorkingTimeTypes[0] : null;
	}

	protected updateEndDateTime() {
		const dialog = $(this.config.dialogId);
		const startDate = parseDateInput(dialog.find(".startDate").val() as string);
		if (startDate != null) {
			const endDate = this.daysDateDifference > 0 ? startDate.add({days: this.daysDateDifference}) : startDate;
			dialog.find(".endDate").val(formatDate(endDate));
		} else {
			dialog.find(".endDate").val(formatDate(null));
		}
	}

	private updateDuration() {
		const startDate = parseDateInput(this.dom.find(".startDate").val() as string);
		const endDate = parseDateInput(this.dom.find(".endDate").val() as string);
		const sameDay = isSameDay(startDate, endDate);
		this.dom.find(".startHalfDayLabel").text(sameDay ? messages["workingTime.halfDay"] : messages["workingTime.startHalfDay"]);
		const endHalfDayInput = this.dom.find(".endHalfDay");
		if (sameDay) {
			endHalfDayInput.prop("checked", false);
		}
		endHalfDayInput.closest("label").toggle(!sameDay);
	}

	private onSelectedWorkingTimeTypeChanged() {
		const workingTimeType = this.getSelectedWorkingTimeType();
		this.editUnit = workingTimeType != null ? workingTimeType.editUnit : DurationUnit.Days;

		this.dom.find(".workingTimeTypeCategory-vacation").toggle(SecurityStatus.isTimeAccountAllowed() &&
			workingTimeType && workingTimeType.category === WorkingTimeTypeCategory.Vacation);
		this.dom.find(".durationUnit-days").toggle(this.editUnit === DurationUnit.Days || this.editUnit === DurationUnit.HalfDays);
		this.dom.find(".durationUnit-halfDays").toggle(this.editUnit === DurationUnit.HalfDays);
		this.dom.find(".dateTimeGroup")
			.toggleClass("col-md-6", this.editUnit === DurationUnit.HalfDays)
			.toggleClass("col-md-12", this.editUnit !== DurationUnit.HalfDays);

		this.descriptionTypeahead.setFilter(workingTimeType ? {workingTimeTypeId: workingTimeType.id} : {});

		this.updateDuration();
		this.updateBorder();
		this.updateTitle();
		this.substituteSelection?.setWorkingTimeType(workingTimeType);
		this.substituteValidation?.setWorkingTimeType(workingTimeType);
	}

	private updateTitle() {
		const workingTimeType = this.getSelectedWorkingTimeType();
		const titleMessage = messages.withArguments("workingTimeDateSpans.dialog." + this.dialogMode, workingTimeType.name);
		this.dialog.setTitle(titleMessage);
	}

	private _delete() {
		const workingTimeType = this.getSelectedWorkingTimeType();

		BootstrapModal.confirm({
			title: "workingtime.delete.title",
			message: messages.withArguments("workingTimeDateSpans.dialog.delete.message",
				workingTimeType.name,
				formatDate(parseISODate(this.workingTimeDateSpan.start)),
				formatDate(parseISODate(this.workingTimeDateSpan.end))
			),
			yesLabel: "confirmation.delete",
			noLabel: "confirmation.cancel",
			yesAction: (modal: BootstrapModal.Modal) => {
				api("workingTimeDateSpans/deleteWorkingTimeDateSpan.json")
					.post({
						workingTimeDateSpanId: this.dom.find(".id").val(),
					})
					.done(() => {
						this.onWorkingTimeDateSpanDeleted();
						this.closeDialog();
					})
					.always(() => {
						modal.close();
					});
			}
		});
	}

	private checkConfigurationAndSave(modal: BootstrapModal.Modal): void {
		if (this.substituteValidation?.hasAlreadySubstitutions()) {
			BootstrapModal.confirm({
				title: "workingTimeDateSpans.dialog.alreadySubstitute.title",
				message: messages.withArguments("workingTimeDateSpans.dialog.alreadySubstitute.info", this.substituteValidation?.getAlreadySubstitutionsUserNames()),
				escapeText: false,
				preventDoubleClickButtons: true,
				yesAction: (modal: BootstrapModal.Modal) => {
					this.save();
					modal.close();
				},
				noAction: (m: BootstrapModal.Modal) => {
					m.close();
					modal.resetLoadingButtons();
				}
			});
		} else {
			this.save();
		}
	}

	private save() {
		api("workingTimeDateSpans/saveWorkingTimeDateSpan.json")
			.options({contentType: "application/json"})
			.post(JSON.stringify({
				...this.workingTimeDateSpan,
				id: this.dom.find(".id").val(),
				version: this.dom.find(".version").val(),
				user: this.userCombobox?.getSelectedItem() ?? this.workingTimeDateSpan.user,
				workingTimeType: this.getSelectedWorkingTimeType(),
				start: getISODate(parseDateInput(this.dom.find(".startDate").val() as string)),
				end: getISODate(parseDateInput(this.dom.find(".endDate").val() as string)),
				description: this.dom.find("#workingTimeDateSpanDialogDescription").val(),
				startHalfDay: this.dom.find(".startHalfDay").prop("checked") as boolean,
				endHalfDay: this.dom.find(".endHalfDay").prop("checked") as boolean,
				substitute: this.substituteSelection?.getSelectedUser()?.id,
			}))
			.done(() => {
				this.onWorkingTimeDateSpanEdited();
				this.closeDialog();
			})
			.fail(() => {
				this.dialog.resetLoadingButtons();
			});
		this.dialog.resetLoadingButtons();
	}

	private initUserCombobox() {
		const userIdElement = this.dom.find(".userId");
		if (userIdElement.length > 0) {
			this.userCombobox = new Combobox<User>({
				items: [],
				formatter: (user) => {
					return user.fullname;
				},
				idElement: userIdElement,
				filterElement: this.dom.find(".userFilter"),
				caretElement: this.dom.find(".userCaret"),
				clearable: false
			});
			$(this.userCombobox).on(Combobox.EVENT_CHANGED, (e, user: User) => {
				this.substituteSelection?.setUser(user?.id);
			});
		}
	}

	private initSubstitutes() {
		this.substituteSelection = document.getElementById("workingTimeDateSpanDialogSubstituteSelection") as SubstituteSelection;
		this.substituteValidation = document.getElementById("workingTimeDateSpanDialogSubstituteValidation") as SubstituteValidation;
		this.substituteSelection?.init();
		this.substituteValidation?.init(false);

		this.initSubstitutesListeners();
	}

	private initSubstitutesListeners() {
		this.substituteSelection?.addEventListener(SubstituteSelection.EVENT_SUBSTITUTE_SELECTED, e => {
			const customEvent = e as CustomEvent<User>;
			this.substituteValidation?.validateSubstitute(customEvent.detail);
		});
		this.substituteSelection?.addEventListener(SubstituteSelection.EVENT_USER_SELECTED, e => {
			const customEvent = e as CustomEvent<number>;
			this.substituteValidation?.validateUser(customEvent.detail);
		});
		this.substituteSelection?.addEventListener(SubstituteSelection.EVENT_SUBSTITUTE_SELECTION_VISIBILITY_TOGGLED, e => {
			const customEvent = e as CustomEvent<boolean>;
			this.substituteValidation?.setRequiresSubstitute(customEvent.detail);
		});
	}

	private initButtonLoadingListeners() {
		this.substituteSelection?.addEventListener(SubstituteSelection.EVENT_START_LOADING, () => this.updateLoadingButtons(1));
		this.substituteValidation?.addEventListener(SubstituteSelection.EVENT_START_LOADING, () => this.updateLoadingButtons(1));
		this.substituteSelection?.addEventListener(SubstituteSelection.EVENT_FINISHED_LOADING, () => this.updateLoadingButtons(-1));
		this.substituteValidation?.addEventListener(SubstituteSelection.EVENT_FINISHED_LOADING, () => this.updateLoadingButtons(-1));
	}

	private updateLoadingButtons(modifyCount: number) {
		this.currentRequestCount += modifyCount;
		if (this.currentRequestCount > 0) {
			const buttons = this.dialog.getButtons("btn-primary");
			Button.setLoading(buttons);
		} else {
			this.currentRequestCount = 0;
			this.dialog.resetLoadingButtons();
		}
	}
}
